import React, { FC, Suspense, useCallback, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { T } from 'src/shared/helpers';
import { Drawer } from 'src/components/widgets/drawer';
import styled from 'styled-components';
import type { helpArticlePanel_ZendeskArticleQuery } from './__generated__/helpArticlePanel_ZendeskArticleQuery.graphql';
import { PanelLinesSkeleton } from '../common/skeletons';

const ArticleBody = styled.div`
    padding: 24px;
    overflow-y: auto;
    /* The panel is fixed-width and can't be stretched, so let the whole panel
       content scroll horizontally when something (e.g. a wide table) overflows. */
    overflow-x: auto;
    flex: 1;
    min-width: 0;
    min-height: 0;
    font-size: 14px;
    line-height: 1.6;
    color: #374151;
    overflow-wrap: break-word;
    word-break: break-word;

    h1,
    h2,
    h3,
    h4 {
        margin-top: 24px;
        margin-bottom: 8px;
        color: #111827;
    }

    h1 {
        font-size: 22px;
    }
    h2 {
        font-size: 18px;
    }
    h3 {
        font-size: 16px;
    }

    p {
        margin-bottom: 12px;
    }

    img {
        max-width: 100%;
        height: auto;
        border-radius: 4px;
        margin: 8px 0;
    }

    ul,
    ol {
        padding-left: 24px;
        margin-bottom: 12px;
    }

    li {
        margin-bottom: 4px;
    }

    a {
        color: #3b82f6;
        text-decoration: none;
        &:hover {
            text-decoration: underline;
        }
    }

    /* Zendesk wraps the table in a <figure> with a fixed inline height/width
       (e.g. "height: 1015px; width: 783px") that is shorter than the table
       actually renders in this narrow panel, so the table spills out of the
       wrapper and overlaps the text below it. Neutralise those fixed dimensions
       on whatever element directly wraps a table, plus the wrapper's default
       margin, and the per-row/cell inline heights below. The table keeps its
       natural width and the panel body scrolls horizontally. */
    :has(> table) {
        height: auto !important;
        width: auto !important;
    }

    figure {
        margin: 0 0 16px 0;
    }

    table {
        border-collapse: collapse;
        height: auto !important;
        margin-bottom: 16px;
    }

    tr {
        height: auto !important;
    }

    th,
    td {
        border: 1px solid #e5e7eb;
        padding: 8px 12px;
        text-align: left;
        vertical-align: top;
        height: auto !important;
    }

    th {
        background: #f9fafb;
        font-weight: 600;
    }

    iframe {
        max-width: 100%;
        border: none;
        border-radius: 4px;
        margin: 8px 0;
    }
`;

const LoadingContainer = styled.div`
    padding: 24px;
`;

export const HelpArticleLoadingFallback: FC = () => (
    <LoadingContainer>
        <PanelLinesSkeleton widths={['60%', '100%', '90%', '95%', '100%', '80%', '70%']} lineHeight='14px' gap='12px' radius='4px' />
    </LoadingContainer>
);

export const HelpArticleContent: FC<{ articleId: string; onTitleLoaded: (title: string) => void }> = ({ articleId, onTitleLoaded }) => {
    const data = useLazyLoadQuery<helpArticlePanel_ZendeskArticleQuery>(
        graphql`
            query helpArticlePanel_ZendeskArticleQuery($id: ID!) {
                zendeskArticle(id: $id) {
                    title
                    body
                }
            }
        `,
        { id: `guid://Zakeke/ZendeskArticle/${articleId}` }
    );

    const title = data?.zendeskArticle?.title || '';
    const body = data?.zendeskArticle?.body || '';
    const containerRef = useRef<HTMLDivElement>(null);
    const titleReported = useRef(false);

    React.useEffect(() => {
        if (title && !titleReported.current) {
            titleReported.current = true;
            onTitleLoaded(title);
        }
    }, [title, onTitleLoaded]);

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            const target = e.target as HTMLElement;
            const anchor = target.closest('a');
            if (!anchor) return;

            const href = anchor.getAttribute('href');
            if (!href) return;

            // Handle anchor links pointing to the same article or just a hash
            if (href.startsWith('#')) {
                e.preventDefault();
                const el = containerRef.current?.querySelector(href);
                el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                return;
            }

            // Handle full Zendesk URLs that point to this same article with a hash
            if (href.includes(`/articles/${articleId}`) && href.includes('#')) {
                e.preventDefault();
                const hash = href.split('#')[1];
                if (hash) {
                    const el = containerRef.current?.querySelector(`#${hash}`);
                    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
                return;
            }

            // External links: open in new tab
            if (href.startsWith('http')) {
                e.preventDefault();
                window.open(href, '_blank', 'noopener,noreferrer');
            }
        },
        [articleId]
    );

    return <ArticleBody ref={containerRef} onClick={handleClick} dangerouslySetInnerHTML={{ __html: body }} />;
};

const ExternalLinkButton = styled.a`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 6px;
    color: #6b7280;
    text-decoration: none;
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
        background: #f3f4f6;
        color: #111827;
    }

    svg {
        width: 16px;
        height: 16px;
    }
`;

export interface HelpArticlePanelProps {
    articleId: string;
    helpUrl: string;
    onClose: () => void;
}

export const HelpArticlePanel: FC<HelpArticlePanelProps> = ({ articleId, helpUrl, onClose }) => {
    const [title, setTitle] = useState(T._('Guide', 'Admin'));
    return (
        <Drawer
            isOpen={true}
            isStacked
            onClose={onClose}
            header={{ title }}
            headerActions={
                <ExternalLinkButton href={helpUrl} target='_blank' rel='noopener noreferrer' title={T._('Open in new tab', 'Admin')}>
                    <svg viewBox='0 0 20 20' fill='currentColor'>
                        <path
                            fillRule='evenodd'
                            d='M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5zm7.25-.75a.75.75 0 01.75-.75h3.5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0V6.31l-5.47 5.47a.75.75 0 01-1.06-1.06l5.47-5.47H12.25a.75.75 0 01-.75-.75z'
                            clipRule='evenodd'
                        />
                    </svg>
                </ExternalLinkButton>
            }
            width={700}
            isResizable={true}
        >
            <Suspense
                fallback={
                    <LoadingContainer>
                        <PanelLinesSkeleton widths={['60%', '100%', '90%', '95%', '100%', '80%', '70%']} lineHeight='14px' gap='8px' radius='4px' />
                    </LoadingContainer>
                }
            >
                <HelpArticleContent articleId={articleId} onTitleLoaded={setTitle} />
            </Suspense>
        </Drawer>
    );
};
